import React, {useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {formatDateTimeShort, GlobalState} from "../../../global/GlobalState";
import {Link} from "react-router-dom";
import {
	Autocomplete,
	Breadcrumbs,
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {
	textColumn,
	tooltipColumn
} from "../../../components/common/grid/Columns";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {GraphQlDataGrid} from "../../../components/common/grid/GraphQlDataGrid";
import {COMPARATOR_CONFIG_OVERVIEW, EXISTS_COMPARATOR_CONFIG_ITEM, PUT_COMPARATOR_CONFIG} from "../Queries";
import {graphQLServices} from "../../../services/GraphQLServices";
import {linkComparatorConfigDetailsFor} from "./ComparatorConfigsDetails";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../ProductModuleOverview";
import {translateFunction} from "../../../localization/TranslationService";
import {GridColDef, GridPagination} from "@mui/x-data-grid";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {cloneMutationData} from "../Utils";
import {ComparatorConfig} from "../models/ComparatorConfig";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";
import {targetStageSafetyCheck} from "../../../libraries/util/SafetyCheck";
import {Configuration} from "../../../global/Configuration";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const ComparatorConfigsOverview = pageAuthorizedByGroup(function () {
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={productsEditorDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
				<Typography color="text.primary">{translate("Comparator Configs")}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<ComparatorConfigsOverviewContent/>
			</Paper>}
		/>
	</>;
}, ["Admin", "Product"]);

export function ComparatorConfigsOverviewContent() {
	const [resetKey, setResetKey] = useState(0);
	const updateGrid = () => setResetKey(resetKey + 1);
	const columns: GridColDef[] = [
		{
			field: "",
			headerName: translate("Link"),
			renderCell: (params) => (
				<Link to={linkComparatorConfigDetailsFor(params.row.krn)} style={{
					textDecoration: "initial"
				}}>
					<Button size="small" tabIndex={-1}>{translate("Open")}</Button>
				</Link>
			),
			sortable: false,
			editable: false,
			disableColumnMenu: false,
			filterable: false,
		},
		{
			field: "clientName",
			headerName: translate("Client Name"),
			minWidth: 330,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "comparatorId",
			headerName: translate("Comparator ID"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedBy",
			headerName: translate("Last Modified By"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			flex: 1,
		},
		{
			field: "lastModifiedDateTime",
			headerName: translate("Last Modified Date/Time"),
			minWidth: 150,
			renderCell: tooltipColumn(textColumn()),
			valueFormatter: (params) => formatDateTimeShort(params.value),
			flex: 1,
		}
	];
	
	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	
	return <GraphQlDataGrid
		rows={[]}
		query={COMPARATOR_CONFIG_OVERVIEW}
		graphFunction={"comparatorConfigs"}
		client={graphQLServices.productsClient}
		rowsPerPageOptions={[50, 100]}
		initialPageSize={50}
		columns={columns}
		getRowId={row => row.clientName + ":" + row.comparatorId}
		queryVariables={{stage, resetKey}}
		slots={{
			footer: CustomFooterFactory(stage, updateGrid)
		}}
	/>;
}

const comparatorIds = [
	"building-home",
	"healthcare",
	"liability",
	"travel-multitrip",
	"triple-play",
	"solar-panels",
	"savings",
	"event-based",
	"car-legacy",
	"business-car",
	"business"
];

function CustomFooterFactory(stage: string, updateGrid: () => void) {
	return function CustomFooter(props: any) {
		const [open, setOpen] = useState(false);
		const [clientName, setClientName] = useState("");
		const [comparatorId, setComparatorId] = useState("");
		const handleClickOpen = () => setOpen(true);
		const handleClose = () => setOpen(false);
		
		const handleCreate = async () => {
			const id = crypto.randomUUID();
			const krn = `krn:${clientName}:comparator-config:${comparatorId}`;
			const newItem = {id, clientName, krn, comparatorId} as ComparatorConfig;
			await comparatorConfigItemExists(newItem).then(async (x) => {
				if (x) {
					throw new Error(`Item already exists`);
				}
				
				await saveChanges(newItem);
				setOpen(false);
				updateGrid();
			});
		};
		
		return <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} style={{
			padding: "0 10px"
		}}>
			<Stack direction={"row"} spacing={2} alignItems={"center"}>
				<Button
					variant={"contained"}
					onClick={handleClickOpen}
					disabled={stage !== productsEditorTargetStage}
				>{translate("Add")}</Button>
			</Stack>
			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>{translate("New ComparatorConfig")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{translate("Please provide the following information.")}
					</DialogContentText>
					<form>
						<Autocomplete
							
							options={comparatorIds}
							autoSelect
							autoComplete
							renderInput={(params) => <TextField
								{...params}
								label={translate("Comparator")}
								variant={"standard"}
							/>}
							getOptionLabel={(it: string) => it}
							onChange={(event, item) => {
								setComparatorId(item ?? "");
							}}
						/>
						<Autocomplete
							options={Configuration.clientNames}
							autoSelect
							freeSolo={true}
							autoComplete
							renderInput={(params) => <TextField
								{...params}
								label={translate("ClientName")}
								variant={"standard"}
							/>}
							getOptionLabel={(it: any) => it}
							onChange={(event, item) => {
								setClientName(item ?? "");
							}}
						/>
						<Stack direction={"row"} justifyContent={"space-between"} style={{marginTop: "0px"}}>
							<Button onClick={handleClose}>{translate("Cancel")}</Button>
							<div style={{flexGrow: 0, padding: "10px"}}>
								<ProcessButton
									buttonProps={
										{disabled: !clientName || !comparatorId}
									}
									defaultText={translate("Create")}
									inProgressText={translate("Saving")}
									completedText={translate("Saved")}
									resetKeys={[clientName, comparatorId]}
									variant={"outlined"}
									onClick={handleCreate}/>
							</div>
						</Stack>
					</form>
				</DialogContent>
			</Dialog>
			<GridPagination {...props} />
		</Stack>;
	};
}

async function saveChanges(comparatorConfig: ComparatorConfig) {
	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	targetStageSafetyCheck(stage);
	
	await graphQLServices.productsClient.mutate({
		mutation: PUT_COMPARATOR_CONFIG,
		variables: {
			stage,
			comparatorConfig: cloneMutationData(comparatorConfig),
		}
	});
}

async function comparatorConfigItemExists(translationItem: ComparatorConfig): Promise<boolean> {
	const existsItems = await graphQLServices.productsClient.query(
		{
			query: EXISTS_COMPARATOR_CONFIG_ITEM,
			variables: {
				stage: productsEditorTargetStage, // should always check on dev instead of other stages
				tenant: translationItem.clientName,
				comparatorId: translationItem.comparatorId
			},
			fetchPolicy: "no-cache"
		}
	);
	const nodes: any[] = existsItems.data.comparatorConfigs.nodes;
	return nodes.length > 0;
}
